import { useEffect, useState } from "react";
import { api, fmtCurrency, fmtDate } from "../../lib/api";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, PieChart, Pie, Cell, AreaChart, Area } from "recharts";
import { Users, CreditCard, CheckCircle2, AlertCircle, ArrowUp, ArrowDown, ChevronDown, AlertTriangle, ShieldCheck } from "lucide-react";
import { Badge } from "../../components/ui/badge";

const COLOR_BY_PROD = {
    NanoCredit: "#00D084",
    FlexCredit: "#0066FF",
    BizCredit: "#FF6B00",
    SalaryAdvance: "#FFD600",
    SplitPay: "#7000FF",
    PrimeCredit: "#FF0055",
    TradeCredit: "#06B6D4",
    StockFinance: "#F59E0B",
    AgroCredit: "#84CC16",
    FleetFinance: "#8B5CF6",
};

const STATUS_COLORS = {
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    active: "bg-blue-50 text-blue-700 border-blue-200",
    "under review": "bg-amber-50 text-amber-700 border-amber-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    repaid: "bg-emerald-50 text-emerald-700 border-emerald-200",
    overdue: "bg-rose-50 text-rose-700 border-rose-200",
    declined: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function AdminDashboard() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/admin/dashboard").then((r) => setData(r.data)); }, []);

    if (!data) return <div className="p-12 text-center text-slate-400">Loading dashboard…</div>;

    const { kpis, portfolio_breakdown, disbursement_trend, recent_applications, risk_buckets, top_regions, collections, alerts } = data;
    const totalDisb = portfolio_breakdown.reduce((s, p) => s + p.amount, 0);

    return (
        <div className="space-y-6" data-testid="admin-dashboard">
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="font-display text-2xl font-semibold text-[#0A1930]">Dashboard Overview</h1>
                    <p className="text-sm text-slate-500 mt-1">Real-time view of your credit operations</p>
                </div>
                <div className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-700 flex items-center gap-1">
                    This Month <ChevronDown size={12} />
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="admin-kpis">
                <Kpi icon={Users} color="#0066FF" label="Total Disbursed" value={fmtCurrency(kpis.total_disbursed, "NGN", { compact: true })} delta={kpis.delta.total_disbursed} />
                <Kpi icon={CreditCard} color="#FF6B00" label="Active Loans" value={kpis.active_loans.toLocaleString()} delta={kpis.delta.active_loans} />
                <Kpi icon={CheckCircle2} color="#00D084" label="Repayment Rate" value={`${kpis.repayment_rate}%`} delta={kpis.delta.repayment_rate} />
                <Kpi icon={AlertCircle} color="#FF0055" label="NPL (30+ Days)" value={`${kpis.npl_30_pct}%`} delta={kpis.delta.npl_30_pct} negative />
            </div>

            {/* Charts row */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <Card title="Disbursement Trend" right={<DateChip>Last 6 Months</DateChip>} testid="card-disbursement-trend">
                    <ResponsiveContainer width="100%" height={260}>
                        <AreaChart data={disbursement_trend}>
                            <defs>
                                <linearGradient id="dt" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#0066FF" stopOpacity={0.4} />
                                    <stop offset="100%" stopColor="#0066FF" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid stroke="#f1f5f9" vertical={false} />
                            <XAxis dataKey="month" axisLine={false} tickLine={false} fontSize={12} />
                            <YAxis axisLine={false} tickLine={false} fontSize={12} tickFormatter={(v) => fmtCurrency(v, "NGN", { compact: true })} />
                            <Tooltip formatter={(v) => fmtCurrency(v, "NGN", { compact: true })} />
                            <Area type="monotone" dataKey="amount" stroke="#0066FF" strokeWidth={2.5} fill="url(#dt)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </Card>

                <Card title="Loan Portfolio by Product" right={<DateChip>Last 6 Months</DateChip>} testid="card-portfolio-breakdown">
                    <div className="grid grid-cols-2 gap-4 items-center">
                        <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                                <Pie data={portfolio_breakdown} dataKey="amount" innerRadius={56} outerRadius={88} paddingAngle={2}>
                                    {portfolio_breakdown.map((p, i) => (
                                        <Cell key={i} fill={COLOR_BY_PROD[p.name] || "#94A3B8"} stroke="#fff" strokeWidth={2} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(v) => fmtCurrency(v, "NGN", { compact: true })} />
                            </PieChart>
                        </ResponsiveContainer>
                        <div>
                            <div className="font-display text-xl font-bold text-[#0A1930]">{fmtCurrency(totalDisb, "NGN", { compact: true })}</div>
                            <div className="text-xs text-slate-500 mb-3">Total</div>
                            <div className="space-y-1.5 max-h-44 overflow-y-auto admin-scroll pr-2">
                                {portfolio_breakdown.map((p) => (
                                    <div key={p.name} className="flex items-center justify-between text-xs" data-testid={`portfolio-row-${p.name}`}>
                                        <div className="flex items-center gap-2">
                                            <span className="size-2.5 rounded-full" style={{ background: COLOR_BY_PROD[p.name] || "#94A3B8" }} />
                                            <span className="font-medium text-slate-700">{p.name}</span>
                                        </div>
                                        <span className="font-bold text-slate-900">{p.pct}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Apps + Risk row */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                <Card title="Recent Loan Applications" right={<a className="text-xs font-bold text-[#0066FF]" href="/admin/loans">View All</a>} className="xl:col-span-2" testid="card-recent-apps">
                    <div className="overflow-x-auto admin-scroll">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wider font-bold text-slate-500 border-b border-slate-100">
                                    <th className="pb-3 pr-4 font-bold">Applicant</th>
                                    <th className="pb-3 pr-4 font-bold">Type</th>
                                    <th className="pb-3 pr-4 font-bold">Amount</th>
                                    <th className="pb-3 pr-4 font-bold">Status</th>
                                    <th className="pb-3 pr-4 font-bold">Applied On</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recent_applications.map((r) => (
                                    <tr key={r.id} className="border-b border-slate-50" data-testid={`app-row-${r.id}`}>
                                        <td className="py-3 pr-4 font-medium text-[#0A1930]">{r.applicant}</td>
                                        <td className="py-3 pr-4">
                                            <span className="px-2 py-1 rounded-md text-xs font-bold" style={{ color: COLOR_BY_PROD[r.product] || "#0066FF", background: `${COLOR_BY_PROD[r.product] || "#0066FF"}15` }}>{r.product}</span>
                                        </td>
                                        <td className="py-3 pr-4 font-mono font-semibold">{fmtCurrency(r.amount)}</td>
                                        <td className="py-3 pr-4"><Badge className={`capitalize border ${STATUS_COLORS[r.status] || "bg-slate-100 text-slate-700"}`}>{r.status}</Badge></td>
                                        <td className="py-3 pr-4 text-slate-500 text-xs">{fmtDate(r.created_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>

                <Card title="Portfolio at Risk" right={<a className="text-xs font-bold text-[#0066FF]" href="/admin/risk">View All</a>} testid="card-portfolio-risk">
                    <div className="space-y-4">
                        {risk_buckets.map((b) => (
                            <div key={b.label} data-testid={`risk-${b.label.replace(/\s/g, "")}`}>
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-sm font-semibold text-[#0A1930]">{b.label}</span>
                                    <span className="text-sm font-bold text-slate-900">{b.pct}%</span>
                                </div>
                                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                                    <div className="h-full rounded-full" style={{ width: `${b.pct}%`, background: b.color }} />
                                </div>
                                <div className="text-xs text-slate-500 mt-1">{fmtCurrency(b.amount, "NGN", { compact: true })}</div>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>

            {/* Bottom row */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                <Card title="Collections Summary" right={<DateChip>This Month</DateChip>} testid="card-collections">
                    <div className="grid grid-cols-3 gap-3 mt-2">
                        <Stat label="Collected" value={fmtCurrency(collections.collected, "NGN", { compact: true })} />
                        <Stat label="Due" value={fmtCurrency(collections.due, "NGN", { compact: true })} />
                        <Stat label="Collection Rate" value={`${collections.collection_rate}%`} />
                    </div>
                </Card>

                <Card title="Top Regions by Disbursement" right={<DateChip>This Month</DateChip>} testid="card-regions">
                    <div className="space-y-3">
                        {top_regions.map((r) => (
                            <div key={r.name} className="flex items-center gap-3" data-testid={`region-${r.name}`}>
                                <div className="w-16 text-xs font-semibold text-slate-700">{r.name}</div>
                                <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                                    <div className="h-full rounded-full" style={{ width: `${(r.amount / top_regions[0].amount) * 100}%`, background: r.color }} />
                                </div>
                                <div className="w-20 text-right text-xs font-bold">{fmtCurrency(r.amount, "NGN", { compact: true })}</div>
                            </div>
                        ))}
                    </div>
                </Card>

                <Card title="Alerts & Notifications" right={<a className="text-xs font-bold text-[#0066FF]" href="#">View All</a>} testid="card-alerts">
                    <div className="space-y-2">
                        {alerts.map((a) => (
                            <Alert key={a.id} {...a} />
                        ))}
                    </div>
                </Card>
            </div>
        </div>
    );
}

function Kpi({ icon: Icon, color, label, value, delta, negative }) {
    const isPositive = delta?.startsWith("+");
    return (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 product-shadow" data-testid={`kpi-${label.replace(/\s/g, "")}`}>
            <div className="flex items-center gap-3">
                <div className="size-10 rounded-xl flex items-center justify-center" style={{ background: `${color}15`, color }}>
                    <Icon size={18} />
                </div>
                <div className="text-xs uppercase tracking-wider font-bold text-slate-500">{label}</div>
            </div>
            <div className="font-display text-2xl font-bold text-[#0A1930] mt-3">{value}</div>
            <div className={`text-xs font-bold mt-1 flex items-center gap-1 ${isPositive ? "text-emerald-600" : "text-rose-600"}`}>
                {isPositive ? <ArrowUp size={12} /> : <ArrowDown size={12} />} {delta} <span className="text-slate-400 font-medium">vs last month</span>
            </div>
        </div>
    );
}

function Card({ title, right, children, className = "", testid }) {
    return (
        <div className={`bg-white rounded-2xl border border-slate-200 p-5 product-shadow ${className}`} data-testid={testid}>
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-semibold text-[#0A1930]">{title}</h3>
                {right}
            </div>
            {children}
        </div>
    );
}

function DateChip({ children }) {
    return <div className="text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-md flex items-center gap-1">{children} <ChevronDown size={11} /></div>;
}

function Stat({ label, value }) {
    return (
        <div>
            <div className="font-display text-xl font-bold text-[#0A1930]">{value}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">{label}</div>
        </div>
    );
}

function Alert({ type, title, subtitle }) {
    const palette = {
        danger: { bg: "bg-rose-50", border: "border-rose-200", color: "text-rose-600", Icon: AlertCircle },
        warning: { bg: "bg-amber-50", border: "border-amber-200", color: "text-amber-600", Icon: AlertTriangle },
        info: { bg: "bg-blue-50", border: "border-blue-200", color: "text-blue-600", Icon: AlertCircle },
        success: { bg: "bg-emerald-50", border: "border-emerald-200", color: "text-emerald-600", Icon: ShieldCheck },
    }[type] || { bg: "bg-slate-50", border: "border-slate-200", color: "text-slate-600", Icon: AlertCircle };
    return (
        <div className={`${palette.bg} border ${palette.border} rounded-xl p-3 flex items-start gap-2`} data-testid={`alert-${type}`}>
            <palette.Icon size={16} className={`${palette.color} flex-shrink-0 mt-0.5`} />
            <div>
                <div className="text-sm font-semibold text-[#0A1930]">{title}</div>
                <div className="text-xs text-slate-600">{subtitle}</div>
            </div>
        </div>
    );
}
